function eachNode(target, callback) {
  if (!target) {
    return [];
  }

  if (typeof target === 'string') {
    return [{ mode: 'selector', value: target, callback: callback }];
  }

  if (target.nodeType === 1) {
    callback(target);
    return [];
  }

  if (typeof target.length === 'number') {
    Array.prototype.forEach.call(target, callback);
  }

  return [];
}

function closest(element, selector) {
  while (element && element.nodeType === 1) {
    if (element.matches(selector)) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

function selectContent(target) {
  var selectedText = '';

  if (!target) {
    return {
      text: '',
      clear: function () {},
    };
  }

  if (target.nodeName === 'INPUT' || target.nodeName === 'TEXTAREA') {
    var readOnly = target.hasAttribute('readonly');
    if (!readOnly) {
      target.setAttribute('readonly', '');
    }
    target.focus();
    target.select();
    target.setSelectionRange(0, target.value.length);
    if (!readOnly) {
      target.removeAttribute('readonly');
    }
    selectedText = target.value;
  } else if (target.nodeName === 'SELECT') {
    target.focus();
    selectedText = target.value;
  } else {
    var selection = window.getSelection();
    var range = document.createRange();
    range.selectNodeContents(target);
    selection.removeAllRanges();
    selection.addRange(range);
    selectedText = selection.toString();
  }

  return {
    text: selectedText,
    clear: function () {
      try {
        window.getSelection().removeAllRanges();
      } catch {
        // ignore
      }
    },
  };
}

function TinyEmitter() {
  this.events = Object.create(null);
}

TinyEmitter.prototype.on = function (name, callback, ctx) {
  (this.events[name] || (this.events[name] = [])).push({
    fn: callback,
    ctx: ctx,
  });
  return this;
};

TinyEmitter.prototype.once = function (name, callback, ctx) {
  var self = this;
  function onceHandler() {
    self.off(name, onceHandler);
    callback.apply(ctx || self, arguments);
  }
  onceHandler._callback = callback;
  return this.on(name, onceHandler, ctx);
};

TinyEmitter.prototype.off = function (name, callback) {
  if (!this.events[name]) {
    return this;
  }
  if (!callback) {
    delete this.events[name];
    return this;
  }
  this.events[name] = this.events[name].filter(function (entry) {
    return entry.fn !== callback && entry.fn._callback !== callback;
  });
  if (!this.events[name].length) {
    delete this.events[name];
  }
  return this;
};

TinyEmitter.prototype.emit = function (name) {
  var args = Array.prototype.slice.call(arguments, 1);
  (this.events[name] || []).slice().forEach(function (entry) {
    entry.fn.apply(entry.ctx || null, args);
  });
  return this;
};

class ClipboardJS extends TinyEmitter {
  constructor(trigger, options) {
    super();
    this.options = options || {};
    this._listeners = [];
    this.resolveOptions();
    this.listen(trigger);
  }

  resolveOptions() {
    var options = this.options;
    this.action =
      typeof options.action === 'function'
        ? options.action
        : function (trigger) {
            return trigger.getAttribute('data-clipboard-action') || 'copy';
          };
    this.target =
      typeof options.target === 'function'
        ? options.target
        : function (trigger) {
            var selector = trigger.getAttribute('data-clipboard-target');
            return selector ? document.querySelector(selector) : null;
          };
    this.text =
      typeof options.text === 'function'
        ? options.text
        : function (trigger) {
            return trigger.getAttribute('data-clipboard-text');
          };
    this.container =
      options.container && options.container.nodeType === 1
        ? options.container
        : document.body;
  }

  listen(trigger) {
    var self = this;
    var delegated = eachNode(trigger, function (node) {
      var handler = function (event) {
        self.onClick(event, node);
      };
      node.addEventListener('click', handler);
      self._listeners.push(function () {
        node.removeEventListener('click', handler);
      });
    });

    delegated.forEach(function (item) {
      var handler = function (event) {
        var matched = closest(event.target, item.value);
        if (!matched) {
          return;
        }
        event.delegateTarget = matched;
        self.onClick(event, matched);
      };
      document.addEventListener('click', handler);
      self._listeners.push(function () {
        document.removeEventListener('click', handler);
      });
    });
  }

  onClick(event, trigger) {
    var action = this.action(trigger);
    var explicitText = this.text(trigger);
    var target = explicitText ? null : this.target(trigger);
    var payload = {
      action: action,
      trigger: trigger,
      clearSelection: function () {
        try {
          trigger && trigger.focus();
        } catch {
          // ignore
        }
      },
    };

    this.copy(action, explicitText, target)
      .then(function (result) {
        payload.text = result.text;
        payload.clearSelection = function () {
          result.clear();
          try {
            trigger && trigger.focus();
          } catch {
            // ignore
          }
        };
        this.emit('success', payload);
      }.bind(this))
      .catch(function (error) {
        payload.error = error;
        payload.text = explicitText || (target ? target.value || target.textContent || '' : '');
        this.emit('error', payload);
      }.bind(this));
  }

  async copy(action, text, target) {
    if (action !== 'copy' && action !== 'cut') {
      throw new Error('Invalid action');
    }

    if (text != null && text !== '') {
      return this.copyText(String(text));
    }

    if (!target) {
      throw new Error('Missing target');
    }

    var selection = selectContent(target);

    try {
      if (
        navigator.clipboard &&
        action === 'copy' &&
        window.isSecureContext &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(selection.text);
      } else if (!document.execCommand(action)) {
        throw new Error('execCommand failed');
      }

      if (
        action === 'cut' &&
        (target.nodeName === 'INPUT' || target.nodeName === 'TEXTAREA') &&
        !target.hasAttribute('readonly') &&
        !target.hasAttribute('disabled')
      ) {
        target.value = '';
      }

      return selection;
    } catch (error) {
      selection.clear();
      throw error;
    }
  }

  async copyText(text) {
    if (
      navigator.clipboard &&
      window.isSecureContext &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      await navigator.clipboard.writeText(text);
      return {
        text: text,
        clear: function () {},
      };
    }

    var textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    this.container.appendChild(textarea);

    var selection = selectContent(textarea);
    var success = document.execCommand('copy');
    this.container.removeChild(textarea);

    if (!success) {
      selection.clear();
      throw new Error('Copy failed');
    }

    return selection;
  }

  destroy() {
    this._listeners.forEach(function (dispose) {
      dispose();
    });
    this._listeners = [];
    this.events = Object.create(null);
  }

  static isSupported(action) {
    var actions = typeof action === 'string' ? [action] : action || ['copy', 'cut'];
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return true;
    }
    if (!document.queryCommandSupported) {
      return false;
    }
    return actions.every(function (name) {
      return document.queryCommandSupported(name);
    });
  }
}

if (typeof window !== 'undefined') {
  window.ClipboardJS = ClipboardJS;
}

export { ClipboardJS };
